import { SelfAccountKeyring } from '../keyring/account_self';
import { create_account_register_keyring_request_raw } from './register';
import { PeerAccountKeyring } from '../keyring/account_peer';
import { compareByteArray } from '../utility';
import { AbstractSelfAccountKeyring } from '../keyring/account_base';

export type create_account_keyring_upgrade_request_result = {
  encryptingSecretEc: Uint8Array;
  encryptingPublicEcSignature: Uint8Array;
  signingSecretEc: Uint8Array;
  mlKem1024Secret: Uint8Array;
  mlKem1024Public: Uint8Array;
  mlKem1024PublicSignature: Uint8Array;
  mlDsa87Secret: Uint8Array;
  mlDsa87Public: Uint8Array;
  ed25519MlDsa87PublicSignature: Uint8Array;
};
export async function create_account_keyring_upgrade_request(
  base_kr: SelfAccountKeyring
): Promise<undefined | create_account_keyring_upgrade_request_result> {
  const upgrade_kr = base_kr.upgrade();
  if (!upgrade_kr) {
    return;
  }
  if (upgrade_kr.get_variant() !== SelfAccountKeyring.VARIANT_ML) {
    throw new Error('Unknown variant for upgrade');
  }
  const register_req = await create_account_register_keyring_request_raw('N/A', upgrade_kr);
  await verify_account_keyring_upgrade_request(
    SelfAccountKeyring,
    base_kr,
    register_req['encrypting_secret_ec'],
    register_req['signing_secret_ec'],
    register_req['encrypting_secret_ml'],
    register_req['signing_secret_ml'],
    register_req['signing_public_ml']
  );
  let peer_kr = await PeerAccountKeyring.load(
    base_kr,
    new TextDecoder().decode(base_kr.id),
    register_req['encrypting_public_ec'],
    register_req['encrypting_public_ec_signature'],
    register_req['signing_public_ec'],
    undefined,
    undefined,
    register_req['encrypting_public_ml'],
    register_req['encrypting_public_ml_signature'],
    register_req['signing_public_ml'],
    register_req['signing_public_ec_ml_signature']
  );
  if (
    peer_kr.get_variant() !== SelfAccountKeyring.VARIANT_ML ||
    !compareByteArray(
      base_kr.get_public_key(SelfAccountKeyring.CURVE25519),
      peer_kr.get_public_key(SelfAccountKeyring.CURVE25519)
    ) ||
    !compareByteArray(
      base_kr.get_public_key(SelfAccountKeyring.ED25519),
      peer_kr.get_public_key(SelfAccountKeyring.ED25519)
    )
  ) {
    throw new Error();
  }
  return {
    encryptingSecretEc: register_req['encrypting_secret_ec'],
    encryptingPublicEcSignature: register_req['encrypting_public_ec_signature'],
    signingSecretEc: register_req['signing_secret_ec'],
    mlKem1024Secret: register_req['encrypting_secret_ml'],
    mlKem1024Public: register_req['encrypting_public_ml'],
    mlKem1024PublicSignature: register_req['encrypting_public_ml_signature'],
    mlDsa87Secret: register_req['signing_secret_ml'],
    mlDsa87Public: register_req['signing_public_ml'],
    ed25519MlDsa87PublicSignature: register_req['signing_public_ec_ml_signature'],
  };
}

export async function verify_account_keyring_upgrade_request(
  self_kr_cls: typeof SelfAccountKeyring, // NOTE: this type is not specifically returned
  base_kr: AbstractSelfAccountKeyring,
  encryptingSecretEc: Uint8Array,
  signingSecretEc: Uint8Array,
  mlKem1024Secret: Uint8Array,
  mlDsa87Secret: Uint8Array,
  mlDsa87Public: Uint8Array
): Promise<SelfAccountKeyring> {
  if (base_kr.get_variant() !== SelfAccountKeyring.VARIANT_BASE) {
    throw new Error();
  }
  const verify_kr = await self_kr_cls.reload_kr_asymmetric(
    base_kr,
    encryptingSecretEc,
    signingSecretEc,
    mlKem1024Secret,
    mlDsa87Secret,
    mlDsa87Public
  );
  if (
    verify_kr.get_variant() !== SelfAccountKeyring.VARIANT_ML ||
    !compareByteArray(
      base_kr.get_public_key(SelfAccountKeyring.CURVE25519),
      verify_kr.get_public_key(SelfAccountKeyring.CURVE25519)
    ) ||
    !compareByteArray(
      base_kr.get_public_key(SelfAccountKeyring.ED25519),
      verify_kr.get_public_key(SelfAccountKeyring.ED25519)
    )
  ) {
    throw new Error();
  }
  return verify_kr;
}
